/**
 * Schematic geometry for the Clearwater Farms Unit 1 irrigation map.
 *
 * Ditches 1-8 run south from Northern Ave to Glendale Ave, with Ditch 1
 * at the west edge (Gate 10-01) and Ditch 8 nearest Citrus (the east
	return `M ${x} ${TOP} L ${x} ${BOTTOM - 2.5}`
 * Ditch 9 runs east along Northern Ave, from Gate 10-01 to the Citrus corner.
 */

export const MAP_VIEWBOX = { width: 1080, height: 840 }

const TOP = 80 // Northern Ave
const BOTTOM = 720 // Glendale Ave
const LEFT = 60 // west edge, Ditch 1 / Gate 10-01
const CITRUS = 1020 // Citrus (east boundary street, not a ditch)

export const roads = { top: TOP, bottom: BOTTOM, left: LEFT, citrus: CITRUS }

// x position of each south-flowing ditch (1-8), evenly spaced; Ditch 1 is the westmost line
export const ditchXPositions: Record<number, number> = {
	1: LEFT,
	2: 180,
	3: 300,
	4: 420,
	5: 540,
	6: 660,
	7: 780,
	8: 900,
}

export const gates: Record<
	string,
	{ x: number; y: number; label: string; ditches: number[] }
> = {
	'10-01': { x: LEFT, y: TOP, label: '10-01', ditches: [1, 2, 3, 4] },
	'10-03': {
		x: ditchXPositions[5]!,
		y: TOP,
		label: '10-03',
		ditches: [5, 6, 7, 8],
	},
}

// gates ordered west-to-east, so each gate's Ditch 9 segment can end where the next one begins
const gatesByX = Object.entries(gates).sort((a, b) => a[1].x - b[1].x)

/** Ditch 9 doesn't run the whole width for a single gate: each gate's water
 * starts at its own gate (which sits where its ditch meets Ditch 9) and
 * heads east to the next gate (or to Citrus, for the easternmost gate). */
function getDitch9Range(entry?: string): { from: number; to: number } {
	if (!entry) return { from: LEFT, to: CITRUS }
	const index = gatesByX.findIndex(([gateEntry]) => gateEntry === entry)
	if (index === -1) return { from: LEFT, to: CITRUS }
	const from = gatesByX[index]![1].x
	const to = gatesByX[index + 1]?.[1].x ?? CITRUS
	return { from, to }
}

/**
 * Returns the x/y coordinate for a given ditch at a fraction (0-1) of its
 * travel from its gate (0) to its final address (1). Pass `entry` for
 * Ditch 9, since its segment depends on which gate is feeding it.
 */
export function getPointOnDitch(
	ditch: number,
	fraction: number,
	entry?: string,
): { x: number; y: number } {
	const f = Math.min(1, Math.max(0, fraction))
	if (ditch === 9) {
		const { from, to } = getDitch9Range(entry)
		return { x: from + f * (to - from), y: TOP }
	}
	const x = ditchXPositions[ditch]
	if (x === undefined) return { x: LEFT, y: TOP }
	return { x, y: TOP + f * (BOTTOM - TOP) }
}

/** SVG path `d` attribute for drawing the given ditch. */
export function getDitchPath(ditch: number, entry?: string): string {
	if (ditch === 9) {
		const { from, to } = getDitch9Range(entry)
		return `M ${from} ${TOP} L ${to} ${TOP}`
	}
	const x = ditchXPositions[ditch]
	return `M ${x} ${TOP} L ${x} ${BOTTOM - 2.5}`
}

// the order ditches are irrigated in, per gate/entry
export const DITCH_ORDER: Record<string, number[]> = {
	'10-01': [9, 1, 2, 3, 4],
	'10-03': [9, 5, 6, 7, 8],
}

export type DitchWindow = { ditch: number; start: number; stop: number }

export type EntryStatusValue = {
	status: 'idle' | 'active' | 'waiting' | 'complete'
	ditch: number | null
	fraction: number
}

type ScheduleRow = {
	entry: string
	ditch: number
	start: Date | string | null
	stop: Date | string | null
}

/** Groups a schedule's rows into contiguous per-ditch time windows, in travel order, for one gate/entry. */
export function buildDitchWindows(
	rows: ScheduleRow[],
	entry: string,
	order: number[],
): DitchWindow[] {
	const windows: DitchWindow[] = []
	for (const ditch of order) {
		const ditchRows = rows.filter(
			(row) =>
				row.entry === entry && row.ditch === ditch && row.start && row.stop,
		)
		if (ditchRows.length === 0) continue
		const starts = ditchRows.map((row) => new Date(row.start!).getTime())
		const stops = ditchRows.map((row) => new Date(row.stop!).getTime())
		windows.push({
			ditch,
			start: Math.min(...starts),
			stop: Math.max(...stops),
		})
	}
	return windows
}

/** Given a gate's ditch windows and a point in time, finds where the water currently is. */
export function computeEntryStatus(
	windows: DitchWindow[],
	order: number[],
	time: number,
): EntryStatusValue {
	if (windows.length === 0) return { status: 'idle', ditch: null, fraction: 0 }

	const active = windows.find((w) => w.start <= time && time <= w.stop)
	if (active) {
		const span = active.stop - active.start
		const fraction = span > 0 ? (time - active.start) / span : 1
		return { status: 'active', ditch: active.ditch, fraction }
	}

	const started = windows.filter((w) => w.start <= time)
	if (started.length === 0) {
		return { status: 'idle', ditch: windows[0]!.ditch, fraction: 0 }
	}

	const current = started[started.length - 1]!
	const isLastDitch = current.ditch === order[order.length - 1]

	if (isLastDitch)
		return { status: 'complete', ditch: current.ditch, fraction: 1 }
	return { status: 'waiting', ditch: current.ditch, fraction: 1 }
}

export type PortAddress = {
	ditch: number
	position: number
	address: number | null
	entry: string | null
	direction?: string
}

/** Groups ports sharing the same physical ditch line, so their positions can be normalized. */
function portGroupKey(port: Pick<PortAddress, 'ditch' | 'entry'>): string {
	return port.ditch === 9 ? `9-${port.entry}` : `${port.ditch}`
}

/** Returns each port's schematic point along its ditch, evenly spaced by position order. */
export function getPortPoints(
	ports: PortAddress[],
): Array<PortAddress & { x: number; y: number; fraction: number }> {
	const groupedPorts = new Map<
		string,
		Array<{ index: number; port: PortAddress }>
	>()
	for (const [index, port] of ports.entries()) {
		const key = portGroupKey(port)
		const group = groupedPorts.get(key) ?? []
		group.push({ index, port })
		groupedPorts.set(key, group)
	}

	const fractions = new Map<number, number>()
	for (const group of groupedPorts.values()) {
		group.sort((a, b) => {
			const aPosition =
				a.port.position === 0 ? Number.POSITIVE_INFINITY : a.port.position
			const bPosition =
				b.port.position === 0 ? Number.POSITIVE_INFINITY : b.port.position
			return aPosition - bPosition
		})
		const lastIndex = group.length - 1
		for (const [index, item] of group.entries()) {
			fractions.set(item.index, lastIndex > 0 ? index / lastIndex : 0)
		}
	}

	return ports.map((port, index) => {
		const fraction = fractions.get(index) ?? 0
		const { x, y } = getPointOnDitch(
			port.ditch,
			fraction,
			port.entry ?? undefined,
		)
		return { ...port, x, y, fraction }
	})
}
